(function (scope) {
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  function strength(value) {
    const p = clamp(Number(value) || 0, 0, 100);
    const anchors = [[0, 0], [10, 1], [15, 2], [17, 3], [24, 4], [32, 4.8], [40, 5]];
    for (let i = 1; i < anchors.length; i++) {
      if (p <= anchors[i][0]) {
        const [start, low] = anchors[i - 1], [end, high] = anchors[i];
        return low + (p - start) / (end - start) * (high - low);
      }
    }
    return 5;
  }
  function rating(value) {
    return Math.min(Number(value) < 40 ? 4.9 : 5, Math.round(strength(value) * 10) / 10);
  }
  function fills(value) {
    const score = strength(value);
    return Array.from({ length: 5 }, (_, i) => clamp(score - i, 0, 1) * 100);
  }
  const api = { strength, rating, fills };
  if (typeof module !== 'undefined') module.exports = api;
  scope.DGProfile = api;
  if (typeof document === 'undefined') return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new Set();
  function setProgress(group, progress) {
    const target = clamp(Number(group.dataset.percentage) || 0, 0, 100);
    const current = target * progress;
    const decimals = Math.min(2, (String(target).split('.')[1] || '').length);
    group.querySelectorAll('[data-count]').forEach(el => el.textContent = current.toFixed(decimals));
    group.querySelectorAll('[data-level]').forEach(el => el.textContent = String(rating(current)));
    group.querySelectorAll('[data-fill]').forEach(el => {
      el.style.width = fills(current)[Number(el.dataset.fill)] + '%';
    });
    group.querySelectorAll('[data-composition]').forEach(el => el.style.setProperty('--composition', current + '%'));
  }
  function animate(group) {
    const key = group.dataset.metricsKey;
    if (group.dataset.played === 'true') return;
    group.dataset.played = 'true';
    const disabled = document.documentElement.dataset.motion === 'off';
    if (reduced.matches || disabled || (key && seen.has(key))) {
      setProgress(group, 1);
      return;
    }
    if (key) seen.add(key);
    group.classList.add('metrics-running');
    const start = performance.now();
    function frame(now) {
      if (!group.isConnected) return;
      const t = clamp((now - start) / 900, 0, 1);
      setProgress(group, 1 - Math.pow(1 - t, 3));
      if (t < 1) requestAnimationFrame(frame);
      else group.classList.remove('metrics-running');
    }
    requestAnimationFrame(frame);
  }
  const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) {
      observer.unobserve(entry.target);
      animate(entry.target);
    }
  }, { threshold: 0.25 }) : null;
  api.init = function (root = document) {
    const groups = [...root.querySelectorAll('[data-metrics]')];
    if (root.matches?.('[data-metrics]')) groups.unshift(root);
    groups.forEach(group => {
      if (group.dataset.metricsReady) return;
      group.dataset.metricsReady = 'true';
      if (seen.has(group.dataset.metricsKey) || reduced.matches || document.documentElement.dataset.motion === 'off') animate(group);
      else if (observer) { setProgress(group, 0); observer.observe(group); }
      else animate(group);
    });
    root.querySelectorAll('[data-gallery]').forEach(gallery => {
      if (gallery.dataset.ready) return;
      gallery.dataset.ready = 'true';
      const track = gallery.querySelector('.gallery-track');
      if (!track) return;
      const items = [...track.children];
      gallery.querySelectorAll('[data-gallery-index]').forEach(button => button.addEventListener('click', () => {
        const item = items[Number(button.dataset.galleryIndex)];
        if (!item) return;
        if (gallery.dataset.layout === 'carousel' || matchMedia('(max-width: 760px)').matches) track.scrollTo({left: item.offsetLeft - track.offsetLeft, behavior: reduced.matches ? 'auto' : 'smooth'});
        else item.scrollIntoView({ behavior: reduced.matches ? 'auto' : 'smooth', block: 'center' });
      }));
      const sync = () => {
        const middle = track.getBoundingClientRect().left + track.clientWidth / 2;
        let nearest = 0, distance = Infinity;
        items.forEach((item, i) => { const r = item.getBoundingClientRect(); const d = Math.abs(r.left + r.width / 2 - middle); if (d < distance) {distance = d; nearest = i;} });
        gallery.querySelectorAll('[data-gallery-index]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.galleryIndex) === nearest)));
      };
      track.addEventListener('scroll', sync, {passive: true});
      sync();
    });
  };
  document.addEventListener('shopify:section:load', event => api.init(event.target));
  document.addEventListener('DOMContentLoaded', () => {
    api.init();
    const mutation = new MutationObserver(records => {
      for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) api.init(node);
    });
    mutation.observe(document.body, {childList: true, subtree: true});
  });
  let lightbox;
  document.addEventListener('click', event => {
    const link = event.target.closest('[data-image-zoom]');
    if (!link) return;
    event.preventDefault();
    if (!lightbox) {
      lightbox = document.createElement('dialog');
      lightbox.className = 'image-lightbox';
      lightbox.setAttribute('aria-label', document.body.dataset.zoomLabel || 'Image detail');
      const close = document.createElement('button');
      close.className = 'dialog-close'; close.type = 'button'; close.textContent = '×';
      close.setAttribute('aria-label', document.body.dataset.closeLabel || 'Close');
      close.addEventListener('click', () => lightbox.close());
      lightbox.append(close, document.createElement('img'));
      lightbox.addEventListener('click', e => {if (e.target === lightbox) lightbox.close();});
      document.body.append(lightbox);
    }
    const img = lightbox.querySelector('img');
    img.src = link.href; img.alt = link.querySelector('img')?.alt || '';
    lightbox.showModal();
  });
})(typeof window === 'undefined' ? globalThis : window);
